/**
 * Coach's To-Do page.
 * Interactive task list with reminders, status tracking, search, and filter.
 */
import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { formatDateUK } from '../lib/utils';
import {
  Plus, Search, Bell, Calendar, Clock, CheckCircle2, RefreshCw, Trash2,
  Edit2, AlertTriangle, CircleDashed, CircleDot, ListTodo, Flag, Filter as FilterIcon, X
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUSES = [
  { value: 'open', label: 'Open', icon: CircleDashed, color: 'bg-blue-100 text-blue-700', dot: 'bg-blue-500' },
  { value: 'in_progress', label: 'In Progress', icon: CircleDot, color: 'bg-amber-100 text-amber-700', dot: 'bg-amber-500' },
  { value: 'closed', label: 'Closed', icon: CheckCircle2, color: 'bg-emerald-100 text-emerald-700', dot: 'bg-emerald-500' }
];

const PRIORITIES = [
  { value: 'low', label: 'Low', color: 'bg-gray-100 text-gray-700' },
  { value: 'medium', label: 'Medium', color: 'bg-blue-100 text-blue-700' },
  { value: 'high', label: 'High', color: 'bg-red-100 text-red-700' }
];

const today = () => new Date().toISOString().split('T')[0];
const isOverdue = (d) => !!d && d < today();
const isDueSoon = (d) => !!d && d >= today() && d <= new Date(Date.now() + 7 * 86400000).toISOString().split('T')[0];
const getStatus = (v) => STATUSES.find(s => s.value === v) || STATUSES[0];
const getPriority = (v) => PRIORITIES.find(p => p.value === v) || PRIORITIES[1];

const blankForm = { title: '', description: '', priority: 'medium', due_date: '', reminder_date: '', follow_up_date: '' };

export default function ToDo() {
  const { user } = useAuth();
  const [todos, setTodos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blankForm);

  useEffect(() => { fetchTodos(); }, []);

  const fetchTodos = async () => {
    try {
      const res = await axios.get(`${API}/todos`);
      setTodos(res.data);
    } catch {
      toast.error('Failed to load to-dos');
    } finally {
      setLoading(false);
    }
  };

  const openAddDialog = () => {
    setEditing(null);
    setForm(blankForm);
    setDialogOpen(true);
  };

  const openEditDialog = (todo) => {
    setEditing(todo);
    setForm({
      title: todo.title || '',
      description: todo.description || '',
      priority: todo.priority || 'medium',
      due_date: todo.due_date || '',
      reminder_date: todo.reminder_date || '',
      follow_up_date: todo.follow_up_date || ''
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.title.trim()) {
      toast.error('Title is required');
      return;
    }
    const payload = {
      title: form.title.trim(),
      description: form.description || null,
      priority: form.priority,
      due_date: form.due_date || null,
      reminder_date: form.reminder_date || null,
      follow_up_date: form.follow_up_date || null
    };
    try {
      if (editing) {
        const res = await axios.patch(`${API}/todos/${editing.id}`, payload);
        setTodos(prev => prev.map(t => t.id === res.data.id ? res.data : t));
        toast.success('To-do updated');
      } else {
        const res = await axios.post(`${API}/todos`, payload);
        setTodos(prev => [res.data, ...prev]);
        toast.success('To-do added');
      }
      setDialogOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    }
  };

  const changeStatus = async (todo, status) => {
    try {
      const res = await axios.patch(`${API}/todos/${todo.id}`, { status });
      setTodos(prev => prev.map(t => t.id === res.data.id ? res.data : t));
      toast.success(`Moved to ${getStatus(status).label}`);
    } catch {
      toast.error('Failed to update status');
    }
  };

  const deleteTodo = async (todo) => {
    if (!window.confirm(`Delete "${todo.title}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/todos/${todo.id}`);
      setTodos(prev => prev.filter(t => t.id !== todo.id));
      toast.success('To-do deleted');
    } catch {
      toast.error('Failed to delete');
    }
  };

  const counts = useMemo(() => {
    return todos.reduce((acc, t) => {
      acc.total += 1;
      acc[t.status] = (acc[t.status] || 0) + 1;
      return acc;
    }, { total: 0, open: 0, in_progress: 0, closed: 0 });
  }, [todos]);

  const reminders = useMemo(() => {
    return todos
      .filter(t => t.status !== 'closed')
      .filter(t => (t.reminder_date && t.reminder_date <= today()) || isOverdue(t.due_date))
      .sort((a, b) => (a.due_date || a.reminder_date || '').localeCompare(b.due_date || b.reminder_date || ''));
  }, [todos]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return todos.filter(t => {
      if (statusFilter !== 'all' && t.status !== statusFilter) return false;
      if (priorityFilter !== 'all' && t.priority !== priorityFilter) return false;
      if (q && !t.title.toLowerCase().includes(q) && !(t.description || '').toLowerCase().includes(q)) return false;
      return true;
    });
  }, [todos, search, statusFilter, priorityFilter]);

  if (!user) return null;

  return (
    <Layout>
      <div className="max-w-5xl mx-auto space-y-6" data-testid="todo-page">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-2xl sm:text-4xl font-bold text-gray-900 font-title tracking-tight">To Do</h1>
            <p className="text-sm text-gray-500 mt-1">Stay on top of your coaching tasks, reminders, and follow-ups.</p>
          </div>
          <Button onClick={openAddDialog} className="btn-primary rounded-xl" data-testid="todo-add-btn">
            <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} />Add Task
          </Button>
        </div>

        {/* Summary */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4" data-testid="todo-summary">
          <SummaryCard label="Total" value={counts.total} icon={ListTodo} color="text-gray-700" />
          <SummaryCard label="Open" value={counts.open} icon={CircleDashed} color="text-blue-600" />
          <SummaryCard label="In Progress" value={counts.in_progress} icon={CircleDot} color="text-amber-600" />
          <SummaryCard label="Closed" value={counts.closed} icon={CheckCircle2} color="text-emerald-600" />
        </div>

        {/* Reminders */}
        {reminders.length > 0 && (
          <Card className="border-amber-200 bg-amber-50/40" data-testid="todo-reminders">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base font-semibold">
                <Bell className="w-4 h-4 text-amber-600" strokeWidth={1.5} />
                Reminders & Overdue ({reminders.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="flex flex-col gap-2">
                {reminders.map(t => (
                  <div
                    key={t.id}
                    className="flex items-center gap-3 p-3 bg-white rounded-xl border border-amber-100 cursor-pointer hover:bg-amber-50/50"
                    onClick={() => openEditDialog(t)}
                    data-testid={`todo-reminder-${t.id}`}
                  >
                    <AlertTriangle className={`w-4 h-4 shrink-0 ${isOverdue(t.due_date) ? 'text-red-500' : 'text-amber-500'}`} strokeWidth={1.5} />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 truncate">{t.title}</p>
                      <p className="text-xs text-gray-500 truncate">
                        {isOverdue(t.due_date) ? `Overdue · due ${formatDateUK(t.due_date)}` : `Reminder ${formatDateUK(t.reminder_date)}`}
                      </p>
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded-full shrink-0 ${getPriority(t.priority).color}`}>
                      {getPriority(t.priority).label}
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Search + Filter */}
        <div className="flex flex-col sm:flex-row gap-2" data-testid="todo-controls">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" strokeWidth={1.5} />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search tasks..."
              className="pl-9 rounded-xl"
              data-testid="todo-search"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full sm:w-44 rounded-xl" data-testid="todo-status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              {STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={priorityFilter} onValueChange={setPriorityFilter}>
            <SelectTrigger className="w-full sm:w-40 rounded-xl" data-testid="todo-priority-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Priorities</SelectItem>
              {PRIORITIES.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
            </SelectContent>
          </Select>
          {(search || statusFilter !== 'all' || priorityFilter !== 'all') && (
            <Button
              type="button"
              variant="ghost"
              onClick={() => { setSearch(''); setStatusFilter('all'); setPriorityFilter('all'); }}
              className="rounded-xl"
              data-testid="todo-clear-filters"
            >
              <X className="w-4 h-4 mr-1" />Clear
            </Button>
          )}
        </div>

        {/* List */}
        {loading ? (
          <div className="text-center py-12 text-gray-400">Loading…</div>
        ) : filtered.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="text-center py-12">
              <ListTodo className="w-12 h-12 mx-auto mb-3 text-gray-300" strokeWidth={1.5} />
              <p className="text-gray-500">{todos.length === 0 ? 'No tasks yet. Add your first one!' : 'No tasks match your filters.'}</p>
            </CardContent>
          </Card>
        ) : (
          <div className="flex flex-col gap-2" data-testid="todo-list">
            {filtered.map(t => <TodoRow key={t.id} todo={t} onEdit={openEditDialog} onDelete={deleteTodo} onChangeStatus={changeStatus} />)}
          </div>
        )}
      </div>

      {/* Add / Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="todo-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Task' : 'Add Task'}</DialogTitle>
            <DialogDescription>Track progress, set reminders, and schedule follow-ups.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Title</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Book the pitch" data-testid="todo-title-input" />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Notes, links, context…" data-testid="todo-desc-input" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Priority</Label>
                <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                  <SelectTrigger data-testid="todo-priority-input"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PRIORITIES.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Due Date</Label>
                <Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="todo-due-input" />
              </div>
              <div>
                <Label>Reminder</Label>
                <Input type="date" value={form.reminder_date} onChange={(e) => setForm({ ...form, reminder_date: e.target.value })} data-testid="todo-reminder-input" />
              </div>
              <div>
                <Label>Follow-up</Label>
                <Input type="date" value={form.follow_up_date} onChange={(e) => setForm({ ...form, follow_up_date: e.target.value })} data-testid="todo-followup-input" />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setDialogOpen(false)} data-testid="todo-cancel-btn">Cancel</Button>
              <Button onClick={handleSave} disabled={!form.title.trim()} className="btn-primary" data-testid="todo-save-btn">
                {editing ? 'Save Changes' : 'Add Task'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}

function SummaryCard({ label, value, icon: Icon, color }) {
  return (
    <Card>
      <CardContent className="p-4 flex items-center gap-3">
        <div className={`w-10 h-10 rounded-xl bg-gray-50 flex items-center justify-center ${color}`}>
          <Icon className="w-5 h-5" strokeWidth={1.5} />
        </div>
        <div>
          <p className="text-xl font-bold text-gray-900">{value}</p>
          <p className="text-xs text-gray-500">{label}</p>
        </div>
      </CardContent>
    </Card>
  );
}

function TodoRow({ todo, onEdit, onDelete, onChangeStatus }) {
  const status = getStatus(todo.status);
  const priority = getPriority(todo.priority);
  const overdue = todo.status !== 'closed' && isOverdue(todo.due_date);
  const dueSoon = todo.status !== 'closed' && isDueSoon(todo.due_date);

  return (
    <div
      className={`bg-white rounded-xl p-3 sm:p-4 border transition-shadow hover:shadow-sm ${
        overdue ? 'border-red-200' : dueSoon ? 'border-amber-200' : 'border-gray-200'
      } ${todo.status === 'closed' ? 'opacity-70' : ''}`}
      data-testid={`todo-item-${todo.id}`}
    >
      <div className="flex items-start gap-3">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${status.color} hover:ring-2 hover:ring-offset-1 hover:ring-emerald-200 transition-shadow`}
              data-testid={`todo-status-btn-${todo.id}`}
              title={status.label}
            >
              <status.icon className="w-4 h-4" strokeWidth={2} />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            {STATUSES.map(s => (
              <DropdownMenuItem
                key={s.value}
                onClick={() => onChangeStatus(todo, s.value)}
                className="cursor-pointer"
                data-testid={`todo-status-option-${todo.id}-${s.value}`}
              >
                <span className={`inline-block w-2 h-2 rounded-full mr-2 ${s.dot}`} />
                {s.label}
                {s.value === todo.status && <span className="ml-auto text-xs text-gray-400">• current</span>}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className={`font-semibold text-gray-900 ${todo.status === 'closed' ? 'line-through text-gray-500' : ''}`}>
              {todo.title}
            </h3>
            <span className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full font-semibold ${priority.color}`}>
              <Flag className="inline w-3 h-3 mr-0.5 -mt-0.5" strokeWidth={2} />{priority.label}
            </span>
            {overdue && <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full font-semibold bg-red-100 text-red-700">Overdue</span>}
          </div>
          {todo.description && <p className="text-sm text-gray-500 mt-1 whitespace-pre-wrap">{todo.description}</p>}
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-xs text-gray-500">
            {todo.due_date && (
              <span className="flex items-center gap-1"><Calendar className="w-3 h-3" strokeWidth={1.5} />Due {formatDateUK(todo.due_date)}</span>
            )}
            {todo.reminder_date && (
              <span className="flex items-center gap-1"><Bell className="w-3 h-3" strokeWidth={1.5} />Remind {formatDateUK(todo.reminder_date)}</span>
            )}
            {todo.follow_up_date && (
              <span className="flex items-center gap-1"><Clock className="w-3 h-3" strokeWidth={1.5} />Follow-up {formatDateUK(todo.follow_up_date)}</span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-1 shrink-0">
          {todo.status === 'closed' ? (
            <Button
              variant="ghost" size="sm"
              onClick={() => onChangeStatus(todo, 'open')}
              className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 rounded-lg"
              data-testid={`todo-reopen-btn-${todo.id}`}
              title="Reopen"
            >
              <RefreshCw className="w-4 h-4" strokeWidth={1.5} />
            </Button>
          ) : (
            <Button
              variant="ghost" size="sm"
              onClick={() => onChangeStatus(todo, 'closed')}
              className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 rounded-lg"
              data-testid={`todo-close-btn-${todo.id}`}
              title="Close"
            >
              <CheckCircle2 className="w-4 h-4" strokeWidth={1.5} />
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={() => onEdit(todo)} className="text-gray-500 hover:text-gray-700 rounded-lg" data-testid={`todo-edit-btn-${todo.id}`} title="Edit">
            <Edit2 className="w-4 h-4" strokeWidth={1.5} />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => onDelete(todo)} className="text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg" data-testid={`todo-delete-btn-${todo.id}`} title="Delete">
            <Trash2 className="w-4 h-4" strokeWidth={1.5} />
          </Button>
        </div>
      </div>
    </div>
  );
}
